import { useRef, useEffect, type JSX } from 'react';
import type { LineHistory } from 'shared';

interface LineHistoryPanelProps {
  isOpen: boolean;
  onClose: () => void;
  lineHistory?: LineHistory;
}

export function LineHistoryPanel({ isOpen, onClose, lineHistory }: LineHistoryPanelProps): JSX.Element | null {
  const panelRef = useRef<HTMLDivElement>(null);

  // Close on backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  // Scroll to bottom when opened
  useEffect(() => {
    if (isOpen && panelRef.current) {
      const chatArea = panelRef.current.querySelector('.line-history__chat');
      if (chatArea) {
        chatArea.scrollTop = chatArea.scrollHeight;
      }
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const conversations = lineHistory?.conversations ?? [];

  return (
    <div className="line-history-overlay" onClick={handleBackdropClick}>
      <div className={`line-history-panel ${isOpen ? 'line-history-panel--open' : ''}`} ref={panelRef}>
        {/* Header */}
        <div className="line-history__header">
          <button className="line-history__close" onClick={onClose}>&times;</button>
          <span className="line-history__title">LINE 履歴</span>
        </div>

        {/* Chat area */}
        <div className="line-history__chat">
          {conversations.length === 0 ? (
            <div className="line-history__empty">
              <p>まだ会話履歴はありません</p>
            </div>
          ) : (
            conversations.map((conv, ci) => (
              <div key={`conv-${ci}`} className="line-history__conversation">
                <div className="line-history__conv-header">
                  <span>{conv.chatTitle}</span>
                </div>
                {conv.messages.map((msg, mi) => (
                  <div
                    key={`msg-${ci}-${mi}`}
                    className={`line-history__bubble line-history__bubble--${msg.side}`}
                  >
                    {msg.side === 'other' && (
                      <span className="line-history__sender">{msg.displayName}</span>
                    )}
                    <div className="line-history__bubble-body">
                      {msg.text}
                    </div>
                  </div>
                ))}
                {conv.userReply && (
                  <div className="line-history__bubble line-history__bubble--self">
                    <div className="line-history__bubble-body">
                      {conv.userReply}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
